import { useCallback, useEffect, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import toast from 'react-hot-toast';

import { itemReviewsApi } from '@/services/itemReviews';
import { PATHS } from '@/routes/paths';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { Badge } from '@/components/ui/badge';
import {
  CardMetric,
  CardMetricWrapper,
  CardMetricHeader,
  CardMetricTitle,
  CardMetricBody,
  CardMetricValue
} from '@/components/ui/card-metric';
import {
  Table,
  TableBody,
  TableCell,
  TableHeaderWrapper,
  TableNoData,
  TableRow
} from '@/components/ui/table';

const STATUS_LABEL = {
  em_revisao:   'Em revisão',
  aprovado:     'Aprovado',
  reprovado:    'Reprovado',
  pede_revisao: 'Pede revisão',
  gerado:       'Recém-gerado'
};

const STATUS_COLOR = {
  em_revisao:   '#fbbf24',
  aprovado:     '#22c55e',
  reprovado:    '#ef4444',
  pede_revisao: '#fb923c',
  gerado:       '#94a3b8'
};

// Cobertura esperada por etapa (descritores). Sincronizar com a matriz
// pedagógica do backend (data/matriz_saeb/matriz_v2.yml).
const EXPECTED_DESCRIPTORS = {
  serie_3_em: 56,
  ano_9_ef:   86,
  ano_5_ef:   83,
  ano_2_ef:   43
};

const ETAPA_VARIANT = {
  serie_3_em: 'info',
  ano_9_ef:   'primary-light',
  ano_5_ef:   'success',
  ano_2_ef:   'attention'
};

const REVIEWERS_HEAD  = [{ label: 'Revisor' }, { label: 'Decisões', width: 120 }];
const BOTTLENECK_HEAD = [
  { label: 'Descritor', width: 150 },
  { label: 'Etapa', width: 130 },
  { label: 'Itens pendentes', width: 140 }
];

export default function ReviewDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await itemReviewsApi.dashboard();
      setData(json);
    } catch (_e) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const breadcrumbs = {
    title: 'Banco de questões',
    nav: [
      { route: PATHS.QUESTIONS.REVIEW_QUEUE, name: 'Revisão pedagógica', isActive: false },
      { name: 'Dashboard', isActive: true }
    ]
  };

  if (loading || !data) {
    return (
      <DashboardContent variant="plain" breadcrumbs={breadcrumbs}>
        <Loader />
      </DashboardContent>
    );
  }

  const totalDecided = (data.by_status?.aprovado || 0) + (data.by_status?.reprovado || 0);
  const totalReviews = Object.values(data.by_status || {}).reduce((a, b) => a + b, 0);
  const approvalRate = totalDecided > 0
    ? Math.round(100 * (data.by_status.aprovado || 0) / totalDecided)
    : 0;

  const byEtapa = data.by_etapa || [];

  return (
    <DashboardContent variant="plain" breadcrumbs={breadcrumbs}>
      {/* Page header */}
      <div className="mb-6">
        <h2 className="mb-1 text-xl font-bold text-foreground">Dashboard de revisão</h2>
        <p className="text-sm text-neutral">Pipeline da revisão pedagógica · todas as etapas</p>
      </div>

      {/* KPIs globais */}
      <CardMetricWrapper className="mb-6">
        <Kpi label="Total no pipeline" value={totalReviews} />
        <Kpi label="Em revisão"        value={data.by_status?.em_revisao || 0} />
        <Kpi label="Aprovados"         value={data.by_status?.aprovado || 0} />
        <Kpi label="% aprovação"       value={`${approvalRate}%`} subtitle="entre decididos" />
      </CardMetricWrapper>

      {/* Por etapa */}
      <section className="mb-6">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Pipeline por etapa</h3>
        {byEtapa.length === 0 ? (
          <EmptyHint>Ainda não há itens em revisão.</EmptyHint>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {byEtapa.map(e => (
              <EtapaCard key={e.etapa} entry={e} />
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Gráfico de status (todas as etapas) */}
        <section className="rounded-lg border border-neutral-lightest bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Distribuição por status (todas as etapas)</h3>
          <HighchartsReact highcharts={Highcharts} options={statusPieOptions(data.by_status)} />
        </section>

        {/* Gráfico empilhado por etapa */}
        <section className="rounded-lg border border-neutral-lightest bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Status por etapa</h3>
          {byEtapa.length === 0 ? (
            <EmptyHint>Sem dados ainda.</EmptyHint>
          ) : (
            <HighchartsReact highcharts={Highcharts} options={stackedBarOptions(byEtapa)} />
          )}
        </section>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Top revisores */}
        <section className="rounded-lg border border-neutral-lightest bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Top revisores</h3>
          <Table>
            <TableHeaderWrapper tableHead={REVIEWERS_HEAD} />
            <TableBody>
              {(data.top_reviewers || []).map(r => (
                <TableRow key={r.reviewer_id}>
                  <TableCell>{r.reviewer_name || `#${r.reviewer_id}`}</TableCell>
                  <TableCell className="tabular-nums">{r.decisions}</TableCell>
                </TableRow>
              ))}
              <TableNoData
                notFound={!data.top_reviewers?.length}
                colSpan={REVIEWERS_HEAD.length}
              />
            </TableBody>
          </Table>
        </section>

        {/* Descritores com gargalo */}
        <section className="rounded-lg border border-neutral-lightest bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Descritores com mais itens pendentes</h3>
          <Table>
            <TableHeaderWrapper tableHead={BOTTLENECK_HEAD} />
            <TableBody>
              {(data.bottleneck_descriptors || []).map(d => (
                <TableRow key={`${d.codigo_descritor}-${d.etapa || 'na'}`}>
                  <TableCell className="font-mono text-xs">{d.codigo_descritor}</TableCell>
                  <TableCell>
                    {d.etapa
                      ? <Badge variant={ETAPA_VARIANT[d.etapa] || 'neutral-lightest'} size="sm">{etapaShort(d.etapa)}</Badge>
                      : <span className="text-xs text-neutral-light">—</span>}
                  </TableCell>
                  <TableCell className="tabular-nums">{d.pendentes}</TableCell>
                </TableRow>
              ))}
              <TableNoData
                notFound={!data.bottleneck_descriptors?.length}
                colSpan={BOTTLENECK_HEAD.length}
              />
            </TableBody>
          </Table>
        </section>
      </div>
    </DashboardContent>
  );
}

function etapaShort(etapa) {
  return ({
    serie_3_em: '3ª EM',
    ano_9_ef:   '9º EF',
    ano_5_ef:   '5º EF',
    ano_2_ef:   '2º EF'
  })[etapa] || etapa;
}

function EtapaCard({ entry }) {
  const expected = EXPECTED_DESCRIPTORS[entry.etapa];
  const approvalDenom = entry.aprovado + entry.reprovado;
  const approvalPct = approvalDenom > 0 ? Math.round((100 * entry.aprovado) / approvalDenom) : 0;
  return (
    <div className="rounded-lg border border-neutral-lightest bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Badge variant={ETAPA_VARIANT[entry.etapa] || 'neutral-lightest'} size="sm">
          {entry.etapa_label}
        </Badge>
        <span className="text-xs text-neutral">
          {expected ? `cobertura ${entry.total}/${expected}` : `${entry.total} itens`}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-y-1 text-sm">
        <StatLine label="Em revisão"   value={entry.em_revisao}   color="text-warning" />
        <StatLine label="Aprovados"    value={entry.aprovado}     color="text-success" />
        <StatLine label="Reprovados"   value={entry.reprovado}    color="text-danger" />
        <StatLine label="Pede revisão" value={entry.pede_revisao} color="text-info" />
      </div>
      {approvalDenom > 0 && (
        <div className="mt-2 border-t border-neutral-lightest pt-2 text-xs text-neutral">
          % aprovação: <span className="font-semibold text-foreground">{approvalPct}%</span> ({approvalDenom} decididos)
        </div>
      )}
    </div>
  );
}

function StatLine({ label, value, color }) {
  return (
    <>
      <span className="text-xs text-neutral">{label}</span>
      <span className={`text-right text-sm font-semibold tabular-nums ${color}`}>{value}</span>
    </>
  );
}

function EmptyHint({ children }) {
  return (
    <div className="rounded-lg border border-neutral-lightest bg-white p-4 text-center text-sm text-neutral">
      {children}
    </div>
  );
}

function Kpi({ label, value, subtitle }) {
  return (
    <CardMetric variant="light">
      <CardMetricHeader>
        <CardMetricTitle>{label}</CardMetricTitle>
      </CardMetricHeader>
      <CardMetricBody>
        <CardMetricValue className="tabular-nums">{value}</CardMetricValue>
        {subtitle && <p className="mt-1 text-xs text-neutral">{subtitle}</p>}
      </CardMetricBody>
    </CardMetric>
  );
}

function statusPieOptions(byStatus) {
  return {
    chart: { type: 'pie', height: 280 },
    title: { text: null },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.y} ({point.percentage:.0f}%)'
        }
      }
    },
    series: [{
      name: 'Itens',
      data: Object.entries(byStatus || {}).map(([k, v]) => ({
        name: STATUS_LABEL[k] || k,
        y: v,
        color: STATUS_COLOR[k] || '#9ca3af'
      }))
    }]
  };
}

function stackedBarOptions(byEtapa) {
  const categories = byEtapa.map(e => e.etapa_label);
  const statuses = ['em_revisao', 'aprovado', 'reprovado', 'pede_revisao'];
  return {
    chart: { type: 'bar', height: 280 },
    title: { text: null },
    credits: { enabled: false },
    xAxis: { categories },
    yAxis: { min: 0, title: { text: 'Itens' }, allowDecimals: false },
    legend: { reversed: true },
    plotOptions: {
      series: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        }
      }
    },
    tooltip: {
      shared: true,
      formatter() {
        const lines = this.points.map(p =>
          `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b>`
        ).join('<br/>');
        return `<b>${this.x}</b><br/>${lines}`;
      }
    },
    series: statuses.map(s => ({
      name: STATUS_LABEL[s],
      color: STATUS_COLOR[s],
      data: byEtapa.map(e => e[s] || 0)
    }))
  };
}
